import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
} from '@mui/material';
import indexedDBService from '../../services/IndexedDBService';
import { useAuth } from '../../context/AuthContext';

interface InventoryItem {
    id: string;
    name: string;
    description?: string;
    price: number;
    hsnCode?: string;
    gstRate?: number;
    unit?: string;
    category?: string;
    userId: string;
    createdAt?: string;
}

interface NewItemDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (item: InventoryItem) => void;
}

const NewItemDialog: React.FC<NewItemDialogProps> = ({ open, onClose, onSave }) => {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<any>({
        name: '',
        description: '',
        price: '',
        hsnCode: '',
        gstRate: 18,
        unit: 'Nos',
        category: '',
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({
            ...prev,
            [name]: (name === 'price' || name === 'gstRate') 
                ? (value === '' ? '' : parseFloat(value)) 
                : value
        }));
    };

    const handleSelectChange = (e: any) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        if (!currentUser?.uid || !formData.name) return;

        try {
            setLoading(true);
            const newItem: InventoryItem = {
                id: `item_${Date.now()}`,
                userId: currentUser.uid,
                createdAt: new Date().toISOString(),
                name: formData.name,
                description: formData.description || '',
                price: Number(formData.price) || 0,
                hsnCode: formData.hsnCode || '',
                gstRate: formData.gstRate || 0,
                unit: formData.unit || 'Nos',
                category: formData.category || '',
            };

            await indexedDBService.putItem('inventory', newItem);
            onSave(newItem);
            onClose();
            // Reset form
            setFormData({
                name: '',
                description: '',
                price: '',
                hsnCode: '',
                gstRate: 18,
                unit: 'Nos',
                category: '',
            });
        } catch (error) {
            console.error('Error saving new item:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add New Item</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Item Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            autoFocus
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            multiline
                            rows={2}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Price"
                            name="price"
                            type="number"
                            value={formData.price}
                            onChange={handleChange}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="HSN Code"
                            name="hsnCode"
                            value={formData.hsnCode}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>GST Rate (%)</InputLabel>
                            <Select
                                label="GST Rate (%)"
                                name="gstRate"
                                value={formData.gstRate}
                                onChange={handleSelectChange}
                            >
                                <MenuItem value={0}>0%</MenuItem>
                                <MenuItem value={5}>5%</MenuItem>
                                <MenuItem value={12}>12%</MenuItem>
                                <MenuItem value={18}>18%</MenuItem>
                                <MenuItem value={28}>28%</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>Unit</InputLabel>
                            <Select
                                label="Unit"
                                name="unit"
                                value={formData.unit}
                                onChange={handleSelectChange}
                            >
                                <MenuItem value="Nos">Nos</MenuItem>
                                <MenuItem value="Pcs">Pcs</MenuItem>
                                <MenuItem value="Kg">Kg</MenuItem>
                                <MenuItem value="Mtr">Mtr</MenuItem>
                                <MenuItem value="Box">Box</MenuItem>
                                <MenuItem value="Set">Set</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!formData.name || loading}
                >
                    Save Item
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewItemDialog;
